// Settings → Эксперты команды.
// Источник: GET /experts, POST /experts, PATCH /experts/{id}, DELETE /experts/{id}.
// Опционально: POST /experts/{id}/invite — отправка приглашения по email.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, Send, CheckCircle2, Inbox } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';

interface Expert {
  id: string;
  user_id: string | null;
  full_name: string;
  email: string | null;
  organization: string | null;
  external: boolean;
  specialties: string[];
  object_types: string[];
  subtypes: string[];
  workload_current: number;
  workload_max: number;
  active: boolean;
  has_account: boolean;
}

interface Specialty {
  code: string;
  title: string;
  object_types: string[];
  subtypes_filter: string[];
}

export function ExpertsBrigadePanel() {
  const qc = useQueryClient();
  const expertsQ = useQuery<Expert[]>({
    queryKey: ['experts'],
    queryFn: () => api.get<Expert[]>('/experts'),
  });
  const specialtiesQ = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: () => api.get<Specialty[]>('/specialties'),
    staleTime: 60 * 60 * 1000,
  });

  const [showAdd, setShowAdd] = useState(false);

  const del = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/experts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['experts'] }); toast.success('Эксперт удалён'); },
    onError: (e) => toast.error(`Ошибка: ${(e as Error).message}`),
  });

  const invite = useMutation({
    mutationFn: (id: string) => api.post<{ token: string; invite_url: string }>(`/experts/${id}/invite`, {}),
    onSuccess: (r) => {
      const url = r.invite_url || `${window.location.origin}/invite/${r.token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast.success('Ссылка приглашения скопирована в буфер');
    },
    onError: (e) => toast.error(`Ошибка: ${(e as Error).message}`),
  });

  const experts = expertsQ.data ?? [];
  const specialties = specialtiesQ.data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h4 style={{ fontSize: 18, fontWeight: 600 }}>Эксперты команды</h4>
          <p style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>
            Справочник специалистов для распределения томов ПД. Эксперт может быть с аккаунтом (видит кабинет) или без (приглашение по email).
          </p>
        </div>
        <Button variant="primary" size="sm" icon={<UserPlus size={16} />} onClick={() => setShowAdd(true)}>
          Добавить эксперта
        </Button>
      </div>

      {experts.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', border: '1px dashed #CBD5E1', borderRadius: 12, color: '#64748B' }}>
          <Inbox size={32} style={{ margin: '0 auto 12px', color: '#94A3B8' }} />
          <div style={{ fontSize: 15, fontWeight: 500 }}>Команда пуста</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Добавьте первого эксперта — без команды распределение томов невозможно.</div>
        </div>
      )}

      {experts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E2E8F0', textAlign: 'left', color: '#64748B' }}>
              <th style={{ padding: '8px 4px' }}>ФИО</th>
              <th style={{ padding: '8px 4px' }}>Email</th>
              <th style={{ padding: '8px 4px' }}>Специальности</th>
              <th style={{ padding: '8px 4px' }}>Нагрузка</th>
              <th style={{ padding: '8px 4px' }}>Аккаунт</th>
              <th style={{ padding: '8px 4px' }}>—</th>
            </tr>
          </thead>
          <tbody>
            {experts.map(e => (
              <tr key={e.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                <td style={{ padding: '8px 4px', fontWeight: 500 }}>{e.full_name}</td>
                <td style={{ padding: '8px 4px', color: '#475569' }}>{e.email || '—'}</td>
                <td style={{ padding: '8px 4px' }}>
                  {e.specialties.map(code => {
                    const sp = specialties.find(s => s.code === code);
                    return (
                      <span key={code} title={sp?.title} style={{
                        display: 'inline-block', padding: '2px 8px', margin: '2px',
                        borderRadius: 999, background: '#EEF2FF', color: '#4338CA', fontSize: 11,
                      }}>{code}</span>
                    );
                  })}
                </td>
                <td style={{ padding: '8px 4px', color: '#475569' }}>
                  {e.workload_current}/{e.workload_max}
                </td>
                <td style={{ padding: '8px 4px' }}>
                  {e.has_account ? (
                    <span style={{ color: '#16A34A', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <CheckCircle2 size={14} /> Есть
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" icon={<Send size={14} />} onClick={() => invite.mutate(e.id)} disabled={invite.isPending}>
                      Пригласить
                    </Button>
                  )}
                </td>
                <td style={{ padding: '8px 4px' }}>
                  <button
                    onClick={() => { if (confirm(`Удалить ${e.full_name}?`)) del.mutate(e.id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}
                  ><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddExpertModal
          specialties={specialties}
          onClose={() => setShowAdd(false)}
          onCreated={() => { qc.invalidateQueries({ queryKey: ['experts'] }); setShowAdd(false); }}
        />
      )}
    </div>
  );
}

function AddExpertModal({ specialties, onClose, onCreated }: { specialties: Specialty[]; onClose: () => void; onCreated: () => void }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());
  const [objectTypes, setObjectTypes] = useState<Set<string>>(new Set());
  const [subtypes, setSubtypes] = useState<Set<string>>(new Set());
  const [workloadMax, setWorkloadMax] = useState(10);

  const create = useMutation({
    mutationFn: () => api.post<Expert>('/experts', {
      full_name: fullName.trim(),
      email: email.trim() || null,
      specialties: Array.from(selectedSpecs),
      object_types: Array.from(objectTypes),
      subtypes: Array.from(subtypes),
      workload_max: workloadMax,
    }),
    onSuccess: () => { toast.success('Эксперт добавлен'); onCreated(); },
    onError: (e) => toast.error(`Ошибка: ${(e as Error).message}`),
  });

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, v: string) => {
    const n = new Set(set);
    if (n.has(v)) n.delete(v); else n.add(v);
    setter(n);
  };

  return (
    <Modal title="Добавить эксперта в команду" onClose={onClose} maxWidth={680}>
      <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="ФИО *">
          <input style={{ width: '100%', padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }} value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Иванов Иван Иванович" />
        </Field>
        <Field label="Email (опционально, для приглашения)">
          <input style={{ width: '100%', padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="expert@example.com" />
        </Field>

        <Field label="Специальности">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto', padding: 8, border: '1px solid #E2E8F0', borderRadius: 6 }}>
            {specialties.map(s => (
              <label key={s.code} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 8px', borderRadius: 999,
                background: selectedSpecs.has(s.code) ? '#EEF2FF' : '#F8FAFC',
                cursor: 'pointer', fontSize: 12,
              }}>
                <input
                  type="checkbox"
                  checked={selectedSpecs.has(s.code)}
                  onChange={() => toggle(selectedSpecs, setSelectedSpecs, s.code)}
                  style={{ margin: 0 }}
                />
                <span>{s.code}</span>
                <span style={{ color: '#94A3B8', fontSize: 11 }}>{s.title.slice(0, 30)}</span>
              </label>
            ))}
          </div>
        </Field>

        <Field label="Типы объектов">
          <div style={{ display: 'flex', gap: 12 }}>
            {(['areal', 'linear'] as const).map(t => (
              <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <input type="checkbox" checked={objectTypes.has(t)} onChange={() => toggle(objectTypes, setObjectTypes, t)} />
                {t === 'areal' ? 'Площадные' : 'Линейные'}
              </label>
            ))}
          </div>
        </Field>

        {objectTypes.has('linear') && (
          <Field label="Подтипы линейных">
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {['railway', 'highway', 'pipeline', 'power_line', 'communication'].map(t => (
                <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                  <input type="checkbox" checked={subtypes.has(t)} onChange={() => toggle(subtypes, setSubtypes, t)} />
                  {t}
                </label>
              ))}
            </div>
          </Field>
        )}

        <Field label="Макс. нагрузка (одновременных проектов)">
          <input type="number" min={1} max={50} value={workloadMax} onChange={e => setWorkloadMax(Number(e.target.value) || 10)} style={{ width: 100, padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }} />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={!fullName.trim() || selectedSpecs.size === 0 || create.isPending}
          >
            {create.isPending ? 'Сохраняем…' : 'Добавить'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}
